import {Component, ElementRef, OnInit, computed, effect, inject, input, signal, viewChild} from '@angular/core';
import {NgTemplateOutlet} from '@angular/common';
import {Router} from '@angular/router';
import {AppRoutes} from '../../enums/routes';
import {GrantChangeNotifierService} from '../../_modules/company/access/grant-change-notifier.service';
import {ViewportService} from '../../_services/viewport.service';
import {AuthenticationService} from '../authentication.service';
import {readErrorCode} from '../two-factor/second-factor-errors';
import {ContextOptionsStore} from './context-options.store';
import {ContextSwitcherPresenceService} from './context-switcher-presence.service';
import {SelfActivationPanelComponent} from './self-activation-panel.component';
import {TileImpersonationPanelComponent} from './tile-impersonation-panel.component';
import {
  TWO_FACTOR_REMEDIATION_URL,
  contextEntryMessageFor,
  isSelfRemediableContextRefusal
} from './context-entry-refusal';
import {
  SelectableContext,
  SelectableContextKind,
  canEnter,
  contextKey,
  contextScope,
  contextTitle,
  isCurrentContext,
  roleLabel,
  viewLabel
} from './context.models';

/**
 * Where a host has placed the switcher, and therefore which of its two shapes it renders.
 *
 * - `floating` — the pill pinned `fixed bottom-4 left-4`, declared once in `app.component.html`. On screen from
 *   Tailwind's `sm` breakpoint **upwards** only.
 * - `header` — the compact chip that lives inside a shell's own top bar. On screen **below** `sm` only.
 *
 * Exactly one of the two is in the DOM at any width; see {@link ContextSwitcherComponent.rendered}.
 */
export type ContextSwitcherPlacement = 'floating' | 'header';

/**
 * The context switcher: **where this account works**, in the app chrome, on every shell.
 *
 * One row per independent view. Management, Employee and Operator are separate applications, so the same shard
 * can legitimately appear more than once and the rows are NOT collapsed — collapsing them would drop somebody
 * into whichever happened to win, which is exactly what the backend's `(company, shard, view)` key exists to
 * prevent.
 *
 * **This is the only session switcher.** The bottom-right *account* switcher it used to be paired with is gone:
 * an account no longer hops between bracketed accounts, it enters a grant-scoped context — so "which account you
 * are" and "where that account acts" collapsed into this one control. Nothing here needs to disambiguate itself
 * from a second switcher any more, and the panel's copy must not send anyone to a corner that is now empty.
 *
 * **The `Plattform` row is a real destination here.** Entering a tenant narrows the session to that context's
 * roles, which drops `PlatformOwner` — so from a tenant session the platform shell is somewhere else, and this
 * is the way back to it (before, only signing out and in again was). The backend does not take the row's word
 * for it: it re-reads the identity and refuses unless it still holds a platform role, which is what makes the
 * offer safe to render from a session that no longer carries the claim.
 *
 * Rendered only for a session that could actually have contexts: `/auth/context/available` reads the subject
 * from the token's `ext_id` claim and refuses a plain password session outright, so a local login never fires
 * the request at all. It also hides itself when there is nothing to choose between — a single context is not a
 * switcher, it is decoration.
 *
 * ## Two placements, one at a time (#602)
 *
 * The floating pill is `position: fixed` in the bottom-left corner. On a desktop that corner is empty; on a
 * phone it is the busiest part of the screen — it sat on top of the „Einladung erstellen" sheet's **Abbrechen**,
 * on the employee drawer's **Ausloggen**, and on the installed app's bottom tab bar. #575 reserved padding in
 * the drawer for it, which fixed one of the three and could never fix the other two: a dialog's own footer and
 * an installed app's tab bar are not this control's to pad.
 *
 * So below `sm` the switcher **leaves the corner entirely** and renders as a chip inside the shell's own top
 * bar, opening downward. From `sm` upwards the floating pill is exactly what it was.
 *
 * Both shapes are this one component, declared once per host (`app.component.html` for the pill,
 * `navbar-office` and the employee panel's bar for the chip) and switched by {@link ViewportService.isBelowSm}
 * rather than by a pair of CSS classes. The distinction matters: CSS would leave every declaration *mounted*,
 * and a mounted switcher is a live participant — it would claim the shared presence signal and, before #602
 * moved the list into {@link ContextOptionsStore}, would have opened its own `/auth/context/available` request.
 * An `@if` over a signal is what makes "one instance active" true rather than merely invisible.
 */
@Component({
  selector: 'auth-context-switcher',
  standalone: true,
  imports: [NgTemplateOutlet, SelfActivationPanelComponent, TileImpersonationPanelComponent],
  // `display: contents` on the host, so this element contributes NO box of its own to a host's layout. It
  // matters at both placements and for opposite reasons: the floating pill has never wanted to be laid out at
  // all, and the header chip has to be laid out by the bar as if the bar had drawn the button itself. Without
  // it the host stays a flex item in both top bars even when nothing renders — a 0 px item that still collects
  // the container's gap, i.e. a stray 1rem hole in the desktop navbar for a control that is not there.
  host: {class: 'contents'},
  styles: [`
    /*
     * The phone drawer's height cap and its entry animation. Written as real CSS rather than as utilities for
     * two reasons, both of which would otherwise be silent failures.
     *
     * 1. THE CAP NEEDS A FALLBACK. "dvh" is the honest unit here — it excludes the browser's own collapsing
     *    toolbars, which "vh" does not — but this app's build floor is iOS 14.5 (see the browserslist note in
     *    CLAUDE.md) and "dvh" only arrives in iOS 15.4. An unsupported unit makes the whole declaration
     *    invalid, so the cap would simply not exist there and the drawer would grow to whatever its content
     *    needs. Two declarations in this order give old engines the "vh" answer and new ones the "dvh" one.
     *    Tailwind cannot express that ordering: it emits utilities in ITS order, not the attribute's.
     *
     *    The cap is measured from the TOP OF THE SCREEN, not from the bar: bar + drawer together never exceed
     *    70 % of the viewport. That is the version that reads as "a drawer", because it is the whole stack a
     *    reader sees hanging from the top edge — a cap of 70 % measured from under the bar would put the
     *    drawer's bottom edge at 76 % and leave a sliver of page that looks like a rendering accident.
     *
     * 2. THE ANIMATION HAS NO PLUGIN BEHIND IT. "tailwindcss-animate" is NOT a dependency of this app, so the
     *    "animate-in slide-in-from-top-2" classes elsewhere in the codebase are inert class names. This is a
     *    real keyframe, switched off wholesale under prefers-reduced-motion rather than merely shortened.
     */
    .context-drawer {
      max-height: calc(70vh - 3.5rem);
      max-height: calc(70dvh - 3.5rem);
      animation: context-drawer-in 200ms cubic-bezier(0.32, 0.72, 0, 1);
    }

    /*
     * THE SCRIM'S HEIGHT IS DECLARED, NOT INFERRED FROM "bottom: 0", and that is not a style preference.
     *
     * Both shells draw their top bar with "backdrop-blur-md", and a backdrop-filter makes an element a
     * containing block for its position:fixed DESCENDANTS — exactly as a transform does. The drawer and this
     * scrim live inside that bar, so "bottom: 0" resolved against the 56px BAR rather than the viewport and
     * the scrim came out 0px tall: it dimmed nothing and, worse, closed nothing, because there was no box to
     * click. Nobody saw it while the scrim had no background.
     *
     * An explicit height is immune to which ancestor won the containing block. Same "dvh" with a "vh" fallback
     * as the drawer above, for the same iOS 14.5 reason.
     */
    .context-scrim {
      height: calc(100vh - 3.5rem);
      height: calc(100dvh - 3.5rem);
    }

    @keyframes context-drawer-in {
      from { transform: translateY(-100%); }
      to { transform: translateY(0); }
    }

    @media (prefers-reduced-motion: reduce) {
      .context-drawer { animation: none; }
    }
  `],
  template: `
    @if (rendered()) {
      @if (isHeader()) {
        <!-- The chip variant. The wrapper is the Escape target: the panel is a descendant of it, so a keypress
             while focus sits inside the panel bubbles to here. "contents" keeps the wrapper out of the bar's
             own flex layout, so the chip is laid out by the bar exactly as the element it replaced was. -->
        <div class="contents" (keydown.escape)="close()">
          <!-- h-11 on the button, h-8 on the chip inside it: the touch target is the full 44 px a phone needs
               while the chip itself stays the ~32 px the top bar has room for. -->
          <button #trigger type="button" (click)="toggle()" [attr.aria-expanded]="open()"
                  aria-haspopup="true" [attr.aria-controls]="open() ? 'context-switcher-panel' : null"
                  data-test="context-chip"
                  class="group flex h-11 min-w-0 max-w-full items-center focus:outline-none">
            <!-- The chip carries the OPEN state, not just the drawer: it is the only part of the control still
                 in the bar once the drawer is down, so it has to look pressed rather than merely present.
                 Tinted border and fill plus a flipped caret — two cues, so the state does not depend on
                 telling pink from grey. -->
            <span class="flex h-8 w-full items-center gap-1.5 rounded-full border-[1px]
                         bg-white/70 pl-1 pr-2.5 text-xs font-semibold text-gray-700 shadow-sm transition-all
                         duration-200 group-hover:border-gray-300 group-hover:bg-white
                         group-focus-visible:outline group-focus-visible:outline-2
                         group-focus-visible:outline-offset-2
                         group-focus-visible:outline-theme-primary-500"
                  [class.border-gray-200]="!open()"
                  [class.border-theme-primary-400]="open()"
                  [class.!bg-theme-primary-50]="open()">
              <span class="flex h-6 w-6 shrink-0 items-center justify-center rounded-full
                           bg-theme-primary-600 text-white" aria-hidden="true">
                <svg class="h-3.5 w-3.5" viewBox="0 0 20 20">
                  <path fill="currentColor"
                        d="M3 5.5A1.5 1.5 0 0 1 4.5 4h4A1.5 1.5 0 0 1 10 5.5v9A1.5 1.5 0 0 1 8.5 16h-4A1.5 1.5 0 0 1 3 14.5v-9Zm8 0A1.5 1.5 0 0 1 12.5 4h3A1.5 1.5 0 0 1 17 5.5v4A1.5 1.5 0 0 1 15.5 11h-3A1.5 1.5 0 0 1 11 9.5v-4Z"/>
                </svg>
              </span>
              <!-- The visible text is a tenant name, which on its own says nothing about what pressing it does.
                   This prefix gives the control an accessible name that names the control. -->
              <span class="sr-only">Arbeitsbereich:</span>
              <span class="truncate">{{ currentLabel() }}</span>
              <span class="shrink-0 text-gray-400 transition-transform duration-200" aria-hidden="true"
                    [class.rotate-180]="open()">▾</span>
            </span>
          </button>

          @if (open()) {
            <!-- The scrim IS the outside-click handler. Closing on the panel's own "focusout" is the other way
                 to write this, and it does not survive contact with this app: a pointer-down on a focusable
                 ancestor takes focus first, so the panel closes before the click it was aimed at ever lands.
                 It starts at the bar's bottom edge, not at the top of the screen: the bar stays live while the
                 drawer is open, because the chip inside it is the control that closes it again. It DIMS the
                 page, so the drawer reads as an overlay rather than as a white block that appeared in the
                 layout — and so the rounded corner cut-outs show dimmed page rather than a stray grey. -->
            <div data-test="context-scrim" (click)="close()" aria-hidden="true"
                 class="context-scrim fixed inset-x-0 top-14 z-[880] bg-black/40"></div>

            <!-- THE CLIP WRAPPER, and it is not decoration: it hugs the bar's bottom edge and hides everything
                 above it, which is what lets the drawer slide out FROM UNDER the bar instead of across it.
                 Without it the drawer's entry animation would be drawn over the bar (it lives inside the bar's
                 own stacking context, so it cannot simply be given a lower z-index than its own ancestor).
                 The wrapper hugs the drawer's height rather than declaring one, so it clips the slide and
                 nothing else — and it paints nothing, so only the drawer itself is ever visible inside its
                 rounded corners.
                 "pointer-events-none" on the wrapper with "pointer-events-auto" back on the drawer: the
                 wrapper's box is RECTANGULAR, so without this it would swallow the taps that land in the two
                 corner cut-outs beside the drawer's rounded edge — the one place a user aims when they mean
                 "close this". Those taps now fall through to the scrim. -->
            <div class="pointer-events-none fixed inset-x-0 top-14 z-[890] overflow-hidden">
              <!-- A TOP DRAWER below "sm", not a floating card: edge to edge, flush against the bar, square at
                   the top because it is continuous with the bar and rounded only where it ends. The width cap
                   and the centring belong to the CHIP; a drawer that is anything less than the full width
                   would read as a popover that happens to be near the top.
                   NO SHADOW AND NO BORDER, both measured rather than chosen. A box-shadow is painted OUTSIDE
                   the rounded border shape — which includes the two corner CUT-OUTS — so it darkened them
                   from the scrim's rgb(149,150,151) to rgb(135,135,136), a soft grey smudge exactly where the
                   dimmed page should show through. A border's arc does the same thing one pixel at a time.
                   The scrim's dim is what separates this drawer from the page; over a 40 % dim a shadow adds
                   nothing a reader can see except those two smudges.
                   The shadow was also "shadow-lg", which is a BOOTSTRAP utility name as well as a Tailwind
                   one — Bootstrap is loaded first and its version carries "!important" (see the style guide's
                   Bootstrap section), so what actually painted was Bootstrap's "0 1rem 3rem rgba(0,0,0,.175)"
                   and no inline override could remove it. -->
              <div #panel id="context-switcher-panel" tabindex="-1"
                   class="context-drawer pointer-events-auto overflow-y-auto rounded-b-2xl bg-white p-2
                          focus:outline-none">
                <ng-container [ngTemplateOutlet]="panelBody"/>
              </div>
            </div>
          }
        </div>
      } @else {
        <div class="fixed bottom-4 left-4 z-[900]">
          <button #trigger type="button" (click)="toggle()" [attr.aria-expanded]="open()"
                  aria-haspopup="true" [attr.aria-controls]="open() ? 'context-switcher-panel' : null"
                  data-test="context-pill"
                  class="inline-flex max-w-[calc(100vw-2rem)] items-center gap-2 rounded-full border-[1px] border-gray-200
                         bg-white px-4 py-2 text-sm font-semibold text-gray-700 shadow-lg transition-all
                         duration-200 hover:border-gray-300 hover:bg-gray-50 active:scale-95
                         focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2
                         focus-visible:outline-theme-primary-500">
            <svg class="h-4 w-4 shrink-0 text-theme-primary-600" viewBox="0 0 20 20" aria-hidden="true">
              <path fill="currentColor"
                    d="M3 5.5A1.5 1.5 0 0 1 4.5 4h4A1.5 1.5 0 0 1 10 5.5v9A1.5 1.5 0 0 1 8.5 16h-4A1.5 1.5 0 0 1 3 14.5v-9Zm8 0A1.5 1.5 0 0 1 12.5 4h3A1.5 1.5 0 0 1 17 5.5v4A1.5 1.5 0 0 1 15.5 11h-3A1.5 1.5 0 0 1 11 9.5v-4Z"/>
            </svg>
            <span class="truncate">{{ currentLabel() }}</span>
            <span class="shrink-0 text-gray-400" aria-hidden="true">▾</span>
          </button>

          @if (open()) {
            <!-- The width is a STYLE binding, not two [class.…] bindings: the Tailwind arbitrary-value class
                 contains brackets and parentheses, which Angular's template parser reads as syntax inside a
                 [class.NAME] binding. The panel widens for the two platform tools because a grant form and an
                 account list do not fit a 22rem column, and shrinking them to fit is how a privilege screen
                 becomes unreadable. Both values stay bounded by the viewport, so a phone is unaffected. -->
            <div id="context-switcher-panel"
                 class="absolute bottom-12 left-0 max-h-[65vh] overflow-y-auto rounded-2xl border-[1px]
                        border-gray-100 bg-white p-2 shadow-2xl"
                 [style.width]="toolOpen() ? 'min(36rem, calc(100vw - 2rem))' : 'min(22rem, calc(100vw - 2rem))'">
              <ng-container [ngTemplateOutlet]="panelBody"/>
            </div>
          }
        </div>
      }
    }

    <!-- ONE panel body, projected into whichever shape is on screen. The rows, the two platform tools and every
         refusal message are identical in both placements — only the box around them moves — so the markup is
         shared rather than kept in step by hand. -->
    <ng-template #panelBody>
      @if (impersonating(); as tile) {
        <button type="button" (click)="impersonating.set(null)"
                class="mb-2 inline-flex items-center gap-1 rounded-xl px-2 py-1 text-xs font-semibold
                       text-gray-600 transition-all duration-200 hover:bg-gray-50">
          &#8592; Zurück zur Bereichsliste
        </button>
        <auth-tile-impersonation-panel [context]="tile" (close)="impersonating.set(null)"/>
      } @else if (selfActivating()) {
        <button type="button" (click)="selfActivating.set(false)"
                class="mb-2 inline-flex items-center gap-1 rounded-xl px-2 py-1 text-xs font-semibold
                       text-gray-600 transition-all duration-200 hover:bg-gray-50">
          &#8592; Zurück zur Bereichsliste
        </button>
        <auth-self-activation-panel (granted)="reload(); grantChanges.notifyChanged()"/>
      } @else {
        <p class="px-2 pt-1 text-xs font-bold uppercase tracking-wide text-gray-500">Arbeitsbereich</p>
        <p class="px-2 pb-2 text-xs text-gray-500">
          Wo dieses Konto arbeitet. Sie bleiben angemeldet — es wechselt nur der <em>Bereich</em>.
        </p>

        @if (error()) {
          <p class="mx-2 mb-2 rounded-xl bg-red-50 px-3 py-2 text-xs font-semibold text-red-700" role="alert">
            {{ error() }}
          </p>
        }

        <ul class="space-y-1">
          @for (context of contexts(); track key(context)) {
            <li>
              <!-- border-[1px], NOT border: Bootstrap's own .border is loaded before styles.scss and
                   sets border-COLOUR with !important, repainting the current card's theme-primary
                   border Bootstrap grey while its tint survives — the card then reads as highlighted
                   but unbordered. Same trap as Bug #234 on the login account selector. -->
              <button type="button" (click)="enter(context)"
                      [disabled]="!enterable(context) || busy()"
                      [attr.aria-current]="current(context) ? 'true' : null"
                      class="w-full rounded-xl border-[1px] p-3 text-left transition-all duration-200
                             disabled:cursor-not-allowed
                             focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2
                             focus-visible:outline-theme-primary-500"
                      [class.opacity-60]="busy() || (!current(context) && !enterable(context))"
                      [class.border-theme-primary-500]="current(context)"
                      [class.bg-theme-primary-50]="current(context)"
                      [class.border-gray-100]="!current(context)"
                      [class.hover:bg-gray-50]="!current(context) && enterable(context)">
                <span class="flex items-baseline justify-between gap-2">
                  <span class="truncate text-sm font-bold text-gray-900">{{ title(context) }}</span>
                  <span class="shrink-0 text-xs font-semibold text-theme-primary-700">{{ view(context) }}</span>
                </span>
                <span class="mt-0.5 block truncate text-xs text-gray-500">{{ scope(context) }}</span>

                @if (context.roles.length) {
                  <span class="mt-2 flex flex-wrap gap-1">
                    @for (role of context.roles; track role) {
                      <!-- The pill tints WITH its card. On the current context the card is
                           bg-theme-primary-50 (#fdf2f8) and a gray-100 pill (#f3f4f6) is a near-identical
                           near-white on it: the silhouette disappears, only the text survives, and the row
                           stops reading as pills at all — which looks like the labels are misaligned against
                           the cards above. theme-primary-100 is one deliberate step darker than the card in
                           the same hue, so the shape reads on both backgrounds. -->
                      <span class="inline-flex items-center rounded-full px-2 py-0.5 text-xs font-semibold
                                   text-gray-600"
                            [class.bg-gray-100]="!current(context)"
                            [class.bg-theme-primary-100]="current(context)">{{ label(role) }}</span>
                    }
                  </span>
                }

                @if (!current(context) && !enterable(context)) {
                  <!-- Defensive only. Every context the backend lists is enterable from here, including the
                       platform shell; this covers a malformed row (a tenant context with no company id)
                       rather than a category of destination. -->
                  <span class="mt-2 block text-xs text-gray-500">
                    Dieser Bereich lässt sich hier nicht öffnen
                  </span>
                }
              </button>

              @if (canImpersonate(context)) {
                <!-- OUTSIDE the card button: a button nested in a button is invalid HTML, and browsers
                     recover by dropping one of the two with no rule about which. -->
                <button type="button" (click)="openImpersonation(context)"
                        class="mt-0.5 w-full rounded-xl px-3 py-1.5 text-left text-xs font-semibold
                               text-theme-primary-700 transition-all duration-200 hover:bg-theme-primary-50
                               focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2
                               focus-visible:outline-theme-primary-500">
                  Konto übernehmen …
                </button>
              }
            </li>
          }
        </ul>

        @if (showChooserLink()) {
          <!-- A second way to the same screen the sign-in flow lands on right after authenticating: this
               account has more than one place to be, and every row already fits in this popover — but the
               chooser is the page-sized version of exactly this list, and some operators would rather work
               from a page than a corner popover, especially on a phone where the popover competes with the
               keyboard for space. -->
          <button type="button" (click)="goToChooser()"
                  class="mt-2 flex w-full items-center justify-center gap-1.5 rounded-xl border-[1px]
                         border-gray-200 px-3 py-2 text-xs font-semibold text-gray-600 transition-all
                         duration-200 hover:bg-gray-50 focus-visible:outline focus-visible:outline-2
                         focus-visible:outline-offset-2 focus-visible:outline-theme-primary-500">
            Zur Bereichsauswahl
          </button>
        }

        @if (requiresSearch()) {
          <div class="mx-2 mt-2 rounded-xl bg-gray-50 px-3 py-2">
            <p class="text-xs text-gray-600">
              Als Plattformverwaltung können Sie grundsätzlich in jeder Firma arbeiten. Diese Liste zeigt nur
              Ihre eigenen Zuweisungen — die vollständige Firmenliste lässt sich hier nicht durchsuchen.
              Über <em>Plattform</em> kehren Sie jederzeit in die Plattformverwaltung zurück.
            </p>
            @if (isPlatformOwner()) {
              <!-- Gated on the SESSION's role, not on requiresSearch: the two agree today, but the button
                   opens a form whose endpoint is gated on PlatformOwner, and a rendered control that 403s
                   is worse than an absent one. -->
              <button type="button" (click)="selfActivating.set(true)"
                      class="mt-2 inline-flex items-center justify-center rounded-2xl bg-theme-primary-600
                             px-4 py-2 text-xs font-semibold text-white shadow-lg
                             shadow-theme-primary-100 transition-all duration-200
                             hover:bg-theme-primary-700 active:scale-95">
                Zugang selbst freischalten
              </button>
            }
          </div>
        }
      }
    </ng-template>
  `
})
export class ContextSwitcherComponent implements OnInit {
  private readonly auth = inject(AuthenticationService);
  private readonly router = inject(Router);
  private readonly presence = inject(ContextSwitcherPresenceService);
  private readonly store = inject(ContextOptionsStore);
  private readonly viewport = inject(ViewportService);

  /** Protected, not private: the template's self-activation host calls {@link GrantChangeNotifierService.notifyChanged} directly. */
  protected readonly grantChanges = inject(GrantChangeNotifierService);

  /**
   * Which shape this declaration renders — see {@link ContextSwitcherPlacement}.
   *
   * Defaulted to `floating` so the app-wide declaration in `app.component.html` reads as it always did, and so
   * a host that forgets to say gets the historical behaviour rather than nothing at all.
   */
  readonly placement = input<ContextSwitcherPlacement>('floating');

  protected readonly open = signal(false);
  protected readonly busy = signal(false);
  protected readonly error = signal<string | null>(null);

  /** The list itself lives in the shared store — see {@link ContextOptionsStore} for why it is not held here. */
  protected readonly contexts = this.store.contexts;
  protected readonly requiresSearch = this.store.requiresSearch;

  /** The tile whose account list has taken over the panel, or null. */
  protected readonly impersonating = signal<SelectableContext | null>(null);

  /** Whether the self-activation form has taken over the panel. */
  protected readonly selfActivating = signal(false);

  private readonly panelElement = viewChild<ElementRef<HTMLElement>>('panel');
  private readonly triggerElement = viewChild<ElementRef<HTMLElement>>('trigger');

  /** Whether this declaration is the in-bar chip rather than the floating corner pill. */
  protected readonly isHeader = computed(() => this.placement() === 'header');

  /**
   * Whether either platform tool is on screen — the panel widens for both.
   *
   * They REPLACE the list rather than unfolding under it, because this control lives in the chrome of every
   * page: an account list appended below the rows would push the panel past its height cap and put the thing
   * being read behind a scroll, on the surface least able to afford one.
   */
  protected readonly toolOpen = computed(() => !!this.impersonating() || this.selfActivating());

  /**
   * Whether there is a genuine choice to offer — independent of where the control is placed.
   *
   * A platform owner qualifies even with a single row, because `requiresSearch` means the row list is not their
   * real list — hiding the control would tell them they have nowhere to go, which is the opposite of true.
   * Hidden while impersonating: the impersonation banner owns the only legitimate exit from that session, and a
   * context change underneath it would orphan the audit session.
   */
  protected readonly visible = computed(() =>
    !this.auth.isImpersonating && (this.contexts().length > 1 || this.requiresSearch()));

  /**
   * Whether THIS declaration is the one on screen: there is something to switch to **and** this placement is
   * the one the current width calls for.
   *
   * The two placements are mutually exclusive by construction — `header` below Tailwind's `sm`, `floating` from
   * `sm` upwards — so the app never has two live switchers, whatever mix of hosts declares one.
   */
  protected readonly rendered = computed(() =>
    this.visible() && (this.isHeader() ? this.viewport.isBelowSm() : !this.viewport.isBelowSm()));

  /**
   * Mirrors the FLOATING pill's presence into the shared {@link ContextSwitcherPresenceService} (#575), so a
   * host layout (today: the employee panel's sidebar) can reserve space for it without asking the backend a
   * second time whether there is anything to switch between.
   *
   * Only the floating placement writes. Two reasons, and the second is the one that bites: the signal means
   * "something is floating over your bottom-left corner", which a chip in a top bar is not — so on a phone it
   * has to read `false` and the reserved gap has to disappear (#602); and a header declaration that wrote its
   * own answer would race the floating one for the same slot, with whichever effect ran last winning.
   *
   * Declared as a field, not in a constructor — the class has none — so it starts running during construction,
   * inside the same injection context {@link inject} already relies on above.
   */
  private readonly syncPresence = effect(() => {
    if (this.placement() !== 'floating') return;
    this.presence.setVisible(this.rendered());
  });

  /**
   * Whether a panel was on screen the last time focus was considered — the memory that lets the closing half of
   * {@link moveFocusWithThePanel} tell "the panel just closed" from "there has never been a panel", so the chip
   * is not focused on the first render of every page.
   */
  private panelWasOpen = false;

  /**
   * Keyboard focus follows the chip's panel: into it when it opens, back onto the chip when it closes.
   *
   * Header placement only. The floating pill is deliberately untouched by #602 — it is the desktop shape, it
   * has no scrim, and moving focus into it would be a behaviour change at a breakpoint that was not meant to
   * change at all.
   */
  private readonly moveFocusWithThePanel = effect(() => {
    const panel = this.panelElement();
    const trigger = this.triggerElement();
    if (!this.isHeader()) return;

    if (panel) {
      panel.nativeElement.focus();
      this.panelWasOpen = true;
      return;
    }

    if (this.panelWasOpen) {
      this.panelWasOpen = false;
      trigger?.nativeElement.focus();
    }
  });

  /** The tenant the session is in, or the invitation to pick one. */
  protected readonly currentLabel = computed(() => this.store.currentLabel() ?? 'Arbeitsbereich wählen');

  /**
   * Whether the panel offers a way to the full-page chooser — only when there is genuinely more than one tile
   * to choose between, matching the wording of the feature it links to ("Wo möchten Sie arbeiten?").
   *
   * `contexts().length`, not {@link visible}: a session with `requiresSearch` but a single row (a platform
   * owner who has not self-activated anywhere yet) has nothing more to see on the full page that is not already
   * in this list, so the link would open a page identical to the popover it came from.
   */
  protected readonly showChooserLink = computed(() => this.contexts().length > 1);

  ngOnInit(): void {
    // Idempotent: up to three hosts declare this component and every one of them is constructed, but the list
    // is fetched once. See ContextOptionsStore.
    this.store.ensureLoaded();
  }

  protected toggle(): void {
    if (this.open()) {
      this.close();
      return;
    }
    this.open.set(true);
  }

  /**
   * Closes the panel and everything that was open inside it.
   *
   * Leaving a tool armed would mean the next press of the chrome button reopens onto an account list instead of
   * the list of workspaces — the control's own label would then be describing something that is not on screen.
   */
  protected close(): void {
    if (!this.open()) return;
    this.open.set(false);
    this.error.set(null);
    this.impersonating.set(null);
    this.selfActivating.set(false);
  }

  /**
   * Whether THIS session may use the two platform affordances.
   *
   * A plain method rather than a `computed`: the session is not a signal, so a `computed` over it would cache
   * its first answer and never recompute. Note the consequence, which is correct rather than a limitation —
   * these appear on the platform shell and are absent inside a tenant, because entering a tenant narrows
   * `PlatformOwner` out of the session and the endpoints behind them would refuse it. The `Plattform` row above
   * is the way back.
   */
  protected isPlatformOwner(): boolean {
    return this.auth.isPlatformOwner;
  }

  /**
   * Whether a row can be a takeover target: a real tenant, and a session that may impersonate.
   *
   * The platform shell has no company whose accounts could be listed. An employee row is excluded because
   * employees are not impersonation targets — the server's target lookup reads the main-DB `User` table — so
   * offering it would render a button whose every press ends in `E_IMPERSONATION_TARGET_NOT_FOUND`.
   */
  protected canImpersonate(context: SelectableContext): boolean {
    if (!this.isPlatformOwner()) return false;
    return context.kind === SelectableContextKind.Company || context.kind === SelectableContextKind.Shard;
  }

  /**
   * Sends this session to the full context chooser page — the router, not `window.open`: unlike entering a
   * context, this changes nothing about which shell is mounted, so a full document reload would only cost a
   * round trip for no reason. Closes the popover first (which also clears whichever tool was open inside it) so
   * the switcher does not sit open behind the page it just navigated away from.
   */
  protected goToChooser(): void {
    this.close();
    // "?pick" tells the chooser to SHOW the tiles rather than auto-select/auto-land (ChooseContextComponent).
    this.router.navigate(['/' + AppRoutes.AuthenticationChooseContext], {queryParams: {pick: 1}}).then();
  }

  /** Hands the panel over to one tile's account list. */
  protected openImpersonation(context: SelectableContext): void {
    this.selfActivating.set(false);
    this.impersonating.set(context);
  }

  /**
   * Re-reads the context list after a self-activation, so the tenant just unlocked appears as a row without a
   * page reload — and returns the panel to that list, which is the thing the operator was trying to reach.
   */
  protected reload(): void {
    this.selfActivating.set(false);
    this.store.reload();
  }

  protected enter(context: SelectableContext): void {
    // `enterable`, not `canEnter`: re-entering the context you are already in would rotate the CM-Secret for
    // no reason and reload the page onto itself.
    if (!this.enterable(context) || this.busy()) return;
    this.busy.set(true);
    this.error.set(null);

    this.auth.enterContext(context).subscribe({
      // A full document load, not a router navigation: the shell that renders is chosen from the session's
      // roles, and the new session's roles can select a different shell entirely.
      next: () => window.open('/', '_self'),
      error: err => {
        const code = readErrorCode(err);

        // The company demands a second factor this account has not enrolled (#282/#279). The remedy is a whole
        // screen, so this is a NAVIGATION rather than a message beside the rows — the same decision
        // `ChooseContextComponent` makes on the same code, deliberately shared rather than restated.
        //
        // The reason rides the URL because this surface reaches the screen from INSIDE a tenant session: it
        // carries whatever roles that context gave it, so without the declaration `AuthGuard` sends it to the
        // shell those roles belong in instead of letting it enrol.
        if (isSelfRemediableContextRefusal(code)) {
          window.open(TWO_FACTOR_REMEDIATION_URL, '_self');
          return;
        }

        this.busy.set(false);
        // German, mapped from the CODE, by the mapping the chooser shares — see `context-entry-refusal.ts`.
        //
        // This line used to read `err?.error?.description`, i.e. the server's developer-facing English, straight
        // into an otherwise fully German panel: the same defect as the shift plan's (#386/#392), on the one
        // control rendered on every page. The chooser had the rule written out twice in its own comments; this
        // component never got it.
        this.error.set(contextEntryMessageFor(code));
      }
    });
  }

  /**
   * Whether this row is the context the session is in right now.
   *
   * Shown as a **theme-primary border plus the tinted card**, not as a text label. The row is `disabled`
   * (see {@link enterable}) and carries `aria-current="true"`, which is what actually announces the state to a
   * screen reader — so the label was redundant there and only ever spoke to sighted users. Two visual cues are
   * kept deliberately rather than colour alone: the border AND the background, so the state does not depend on
   * distinguishing pink from grey. The row is also excluded from the disabled dimming, because it is disabled
   * for "you are already here" rather than "this is unavailable", and dimming it would fade the very border
   * that marks it.
   *
   * Compared against the **whole** identity of the session's context, not just its tenant. Two cards of one
   * company that differ only in view — the employee area and the management area — were both marked before, and
   * because a marked card is disabled, neither was reachable from the other. See {@link isCurrentContext}.
   */
  protected current(context: SelectableContext): boolean {
    return isCurrentContext(context, this.auth.userValue);
  }

  /**
   * Whether this row is a place this session can go.
   *
   * **The platform shell is one of them here**, unlike in {@link ChooseContextComponent}. The difference is not
   * cosmetic: this switcher only ever renders inside a session that has *already* entered a context, and
   * entering one narrows the token to that context's roles — dropping `PlatformOwner`. So a tenant session is
   * genuinely somewhere else, and the platform shell is genuinely a destination. The chooser runs at the
   * opposite moment, when a platform principal still holds the platform session, and says "Sie sind bereits
   * hier" because that is true there.
   *
   * The backend authorizes it on its own terms (the identity is re-read from the database and must still hold
   * a platform role), so a row rendered here is an offer, never a grant.
   */
  protected enterable(context: SelectableContext): boolean {
    return canEnter(context) && !this.current(context);
  }

  // Template-facing thin wrappers over the pure helpers, so the template stays free of imports.
  protected key = contextKey;
  protected title = contextTitle;
  protected scope = contextScope;
  protected label = roleLabel;

  protected view(context: SelectableContext): string {
    return viewLabel(context.view);
  }
}
